import {
    ComponentPropsWithoutRef,
    ForwardedRef,
    ReactNode,
    createRef,
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { renderToString } from 'react-dom/server';
import { FilePond } from 'react-filepond';
import { Link, useHistory } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { maskWholeNumber, pluralize } from '@givebutter/util';
import {
    faEdit,
    faEye,
    faPen,
    faPlus,
    faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { faCheckCircle, faEnvelopeOpenText } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { Editor as TinyEditor } from '@tinymce/tinymce-react/lib/cjs/main/ts/components/Editor';
import {
    deleteEngageMessageMedia,
    getEngageSenders,
    getMessageNumbers,
    uploadEngageMessageMedia,
} from 'api/engage';
import { getFile, getMediaPreview } from 'api/media';
import { AdvancedEmailEditor as PrimitiveAdvancedEmailEditor } from 'assets/js/pages/engage/AdvancedEmailEditor';
import * as Form from 'components/form';
import {
    MessageBuilderContext,
    MessageBuilderContextValues,
} from 'components/message-builder/MessageBuilderContext';
import { SmsMessagePreview } from 'components/message-builder/SmsMessagePreview';
import * as Tooltip from 'components/tooltip';
import { Upload } from 'components/upload';
import * as WysiwygComp from 'components/wysiwyg';
import { UseFormWysiwyg } from 'components/wysiwyg/UseFormWysiwyg';
import { Settings } from 'constants/settings';
import { useAppState } from 'customHooks/useAppState';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentBuilderMessage } from 'customHooks/useCurrentBuilderMessage';
import { getFormattedPhoneNumber } from 'helpers/formatting';
import IframeResizer from 'iframe-resizer-react';
import GivebutterPlusLogo from 'images/givebutter-plus-logo.svg';
import { first, round } from 'lodash';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import sanitizeHtml from 'sanitize-html';
import { twJoin, twMerge } from 'tailwind-merge';
import { EngageMessageMedia } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const CHARACTER_LIMIT_COUNT = 450;
const CHARACTER_SET_WARNING_COUNT = 70;

const SMS_TOOLBAR = ['aidialog', 'aishortcuts', 'emoticons', 'mergetags'];
const EMAIL_TOOLBAR = [
    'aidialog',
    'aishortcuts',
    'undo',
    'redo',
    'styles',
    'align',
    'bold',
    'italic',
    'underline',
    'strikethrough',
    'forecolor',
    'backcolor',
    'link',
    'mergetags',
    'canva',
    'image',
    'media',
    'emoticons',
    'numlist',
    'bullist',
    'hr',
];

interface MessageBuilderRootProps
    extends ComponentPropsWithoutRef<'div'>,
        MessageBuilderContextValues {}
const MessageBuilderRoot = forwardRef(
    (
        { variant, messageId, className, ...restProps }: MessageBuilderRootProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const value = useMemo(() => ({ variant, messageId }), [variant, messageId]);
        return (
            <MessageBuilderContext.Provider value={value}>
                <Box
                    className={twMerge('max-w-[calc(100vw_-_2rem)]', className)}
                    ref={forwardedRef}
                    {...restProps}
                />
            </MessageBuilderContext.Provider>
        );
    },
);

interface MessageBuilderHeaderProps extends ComponentPropsWithoutRef<'div'> {}
const MessageBuilderHeader = forwardRef(
    (
        { children, className, ...restProps }: MessageBuilderHeaderProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { variant } = useContext(MessageBuilderContext);
        return (
            <div
                className={twMerge(
                    'grid min-h-14 grid-cols-3 items-center border-b border-gray-100 px-4 py-2',
                    className,
                )}
                ref={forwardedRef}
                {...restProps}
            >
                {variant === 'email' ? (
                    <svg
                        fill="none"
                        height="13"
                        viewBox="0 0 52 13"
                        width="52"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <rect fill="#fe5a55" height="12" rx="6" width="12" y=".619141" />
                        <rect fill="#fdb935" height="12" rx="6" width="12" x="20" y=".619141" />
                        <rect fill="#33c749" height="12" rx="6" width="12" x="40" y=".619141" />
                    </svg>
                ) : (
                    <div></div>
                )}
                {children}
            </div>
        );
    },
);

interface MessageBuilderHeaderActionsProps extends ComponentPropsWithoutRef<'div'> {}
const MessageBuilderHeaderActions = forwardRef(
    (
        { className, ...restProps }: MessageBuilderHeaderActionsProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        return (
            <div
                className={twMerge('flex items-center justify-end gap-2', className)}
                ref={forwardedRef}
                {...restProps}
            />
        );
    },
);

interface MessageBuilderHeaderNameProps extends ComponentPropsWithoutRef<'div'> {
    children?: never;
}
const MessageBuilderHeaderName = forwardRef(
    (
        { className, ...restProps }: MessageBuilderHeaderNameProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const [isEditing, setIsEditing] = useState(false);
        const { control, watch, path } = Form.useGivebutterFormContext();
        const internalName = watch('internal_name');

        return (
            <div
                className={twMerge('flex justify-center', className)}
                ref={forwardedRef}
                {...restProps}
            >
                {!internalName || isEditing ? (
                    <Form.Container>
                        <Form.Label name={path('internal_name')} className="sr-only">
                            Internal Name
                        </Form.Label>
                        <Form.TextInputRoot
                            control={control}
                            name="internal_name"
                            rules={{ required: 'Message name is required' }}
                        >
                            <Form.TextInputForm
                                autoFocus
                                onBlur={() => setIsEditing(false)}
                                onFocus={() => setIsEditing(true)}
                                onKeyUp={e => {
                                    if (e.key === 'Enter' || e.key === 'Escape') {
                                        setIsEditing(false);
                                    }
                                }}
                            />
                        </Form.TextInputRoot>
                        <Form.Error name={path('internal_name')} />
                    </Form.Container>
                ) : (
                    <Button
                        variant="transparent"
                        size="sm"
                        onClick={e => {
                            e.stopPropagation();
                            setIsEditing(true);
                        }}
                    >
                        <span>{internalName}</span>
                        <FontAwesomeIcon icon={faPen} />
                    </Button>
                )}
            </div>
        );
    },
);

interface MessageBuilderBodyProps extends ComponentPropsWithoutRef<'div'> {}
const MessageBuilderBody = forwardRef(
    (
        { children, className, ...restProps }: MessageBuilderBodyProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { variant } = useContext(MessageBuilderContext);

        return (
            <div
                className={twMerge('flex w-full flex-col gap-8 px-6 lg:flex-row', className)}
                ref={forwardedRef}
                {...restProps}
            >
                {variant === 'sms' ? (
                    <>
                        <div className="my-3 min-w-80 flex-grow">{children}</div>
                        <div className="my-3 flex-shrink-0 flex-grow-0">
                            <MessageBuilderSmsPreview />
                        </div>
                    </>
                ) : null}
                {variant === 'email' ? (
                    <div className="my-3 w-full flex-grow">{children}</div>
                ) : null}
            </div>
        );
    },
);

interface MessageBuilderToProps extends ComponentPropsWithoutRef<'div'> {}
const MessageBuilderTo = forwardRef(
    (
        { children, className, ...restProps }: MessageBuilderToProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { data: message } = useCurrentBuilderMessage();

        return (
            <div
                className={twMerge(
                    'grid grid-cols-[80px_1fr] items-center border-b border-b-gray-100 py-2.5',
                    className,
                )}
                ref={forwardedRef}
                {...restProps}
            >
                <span className="text-sm text-gray-400">To:</span>
                <div className="flex">
                    <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                            <p className="flex items-center gap-4 text-sm">
                                {maskWholeNumber(message.recipient_count)}{' '}
                                {pluralize(message.recipient_count, 'contact')}
                                {message.recipient_excluded_count > 0 && (
                                    <span className="text-gray-400">
                                        ({maskWholeNumber(message.recipient_excluded_count)}{' '}
                                        excluded)
                                    </span>
                                )}
                            </p>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content inverse>
                                <p>
                                    Messages will be sent to contacts matching your filters at the
                                    time of sending, less those who have not opted-in or have
                                    unsubscribed.
                                </p>
                                <Tooltip.Arrow inverse />
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
            </div>
        );
    },
);

interface MessageBuilderFromProps extends ComponentPropsWithoutRef<'div'> {}
const MessageBuilderFrom = forwardRef(
    (
        { children, className, ...restProps }: MessageBuilderFromProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { variant } = useContext(MessageBuilderContext);

        return (
            <div
                className={twMerge(
                    'grid grid-cols-[80px_1fr] items-center border-b border-b-gray-100 py-2.5',
                    className,
                )}
                ref={forwardedRef}
                {...restProps}
            >
                <span className="text-sm text-gray-400">From:</span>

                {children || variant === 'sms' ? (
                    <MessageBuilderSmsSender />
                ) : (
                    <MessageBuilderEmailSender />
                )}
            </div>
        );
    },
);

const MessageBuilderSmsSender = () => {
    const { tasks, accountId } = useCurrentAccount();

    const { data: numbers } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageNumbers(accountId),
        queryFn: () => getMessageNumbers(accountId),
    });

    const { control, setValue, watch, trigger } = Form.useGivebutterFormContext();
    const senderId = watch('sender_id');

    if (!senderId && numbers.length) {
        setValue('sender_id', numbers[0].id);
    }

    useEffect(() => {
        trigger();
    }, [senderId, trigger]);

    return (
        <>
            {!tasks.engage.has_address ? (
                <span className="flex items-center gap-1.5 text-sm text-red-600">
                    <FontAwesomeIcon icon={faTriangleExclamation} />

                    <span>
                        Visit{' '}
                        <Link
                            to={`/accounts/${accountId}/engage/settings/addresses`}
                            className="text-red-600 underline"
                        >
                            Engage Settings
                        </Link>{' '}
                        to add your organization address.
                    </span>
                </span>
            ) : null}

            {tasks.engage.has_address && !numbers[0] ? (
                <span className="flex items-center gap-1.5 text-sm text-red-600">
                    <FontAwesomeIcon icon={faTriangleExclamation} />

                    <span>
                        Visit{' '}
                        <Link
                            to={`/accounts/${accountId}/engage/settings/sms`}
                            className="text-red-600 underline"
                        >
                            Engage Settings
                        </Link>{' '}
                        to add an SMS number.
                    </span>
                </span>
            ) : null}

            {tasks.engage.has_address && numbers[0] ? (
                <span className="text-sm">{getFormattedPhoneNumber(numbers[0].value)}</span>
            ) : null}

            <div className="hidden">
                <Form.TextInputRoot
                    name="sender_id"
                    control={control}
                    rules={{ required: 'Sender is required' }}
                >
                    <Form.TextInputForm />
                </Form.TextInputRoot>
            </div>
        </>
    );
};

const MessageBuilderEmailSender = () => {
    const { accountId } = useCurrentAccount();
    const { data: senders } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageSenders(accountId),
        queryFn: () => getEngageSenders(accountId),
        select: response => response.data.data,
    });
    const { control, path, watch, setValue } = Form.useGivebutterFormContext();

    const sender_id = watch('sender_id');
    const availableSenders = senders.filter(s => s.verified_at);
    const hasAvailableSenders = availableSenders.length > 0;

    useEffect(() => {
        if (availableSenders.length > 0 && !sender_id) {
            setTimeout(() => {
                setValue('sender_id', first(availableSenders)?.id.toString(), {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                });
            }, 0);
        }
    }, [availableSenders, sender_id, setValue]);

    return (
        <>
            <div className={twMerge('hidden', !hasAvailableSenders && 'block')}>
                <p className="text-sm">
                    You need to{' '}
                    <Link to={`/accounts/${accountId}/engage/settings/senders`}>add a sender</Link>{' '}
                    before continuing.
                </p>
            </div>

            <div className={twMerge('hidden', hasAvailableSenders && 'block min-w-0')}>
                <Form.Container className="max-w-xs">
                    <Form.Label name={path('sender_id')} className="sr-only">
                        From
                    </Form.Label>
                    <Form.SingleSelect
                        control={control}
                        name="sender_id"
                        rules={{ required: 'Sender is required' }}
                    >
                        <SingleSelect.Trigger aria-label="Sender" size="sm" />
                        <SingleSelect.Content>
                            {availableSenders.map(sender => (
                                <SingleSelect.Item
                                    value={sender.id.toString()}
                                    key={sender.id}
                                    className="flex-col items-start gap-1"
                                >
                                    <span>{sender.value}</span>
                                    <span className="text-xs text-gray-400">
                                        <SingleSelect.ItemText>
                                            {sender.email}
                                        </SingleSelect.ItemText>
                                    </span>
                                </SingleSelect.Item>
                            ))}
                        </SingleSelect.Content>
                    </Form.SingleSelect>
                    <Form.Error name={path('sender_id')} />
                </Form.Container>
            </div>
        </>
    );
};

interface MessageBuilderSubjectProps extends ComponentPropsWithoutRef<'div'> {}
const MessageBuilderSubject = forwardRef(
    (
        { children, className, ...restProps }: MessageBuilderSubjectProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { control, path } = Form.useGivebutterFormContext();
        return (
            <div
                className={twMerge(
                    'grid grid-cols-[80px_1fr] items-center border-b border-b-gray-100 py-2.5',
                    className,
                )}
                ref={forwardedRef}
                {...restProps}
            >
                <span className="text-sm text-gray-400">Subject:</span>
                <Form.Container className="max-w-xs">
                    <Form.Label name={path('subject')} className="sr-only">
                        Subject
                    </Form.Label>
                    <Form.TextInputRoot
                        control={control}
                        name="subject"
                        rules={{ required: 'Subject is required' }}
                        inputRootProps={{ size: 'sm' }}
                    >
                        <Form.TextInputForm placeholder="What's the subject?" />
                    </Form.TextInputRoot>
                    <Form.Error name={path('subject')} />
                </Form.Container>
            </div>
        );
    },
);

interface MessageBuilderEmailFilesProps extends ComponentPropsWithoutRef<'div'> {}
const MessageBuilderEmailFiles = forwardRef(
    (
        { children, className, ...restProps }: MessageBuilderEmailFilesProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { accountId } = useCurrentAccount();
        const { error } = useToast();
        const ref = createRef<FilePond>();
        const { messageId } = useContext(MessageBuilderContext);

        const { data: message } = useCurrentBuilderMessage();

        const noImages = message.media.length === 0;
        const attachment = message && `(${round(message.media_size / 1000)} MB of 5 MB used)`;
        const tooltip = `We support images, PDFs and MS Office documents. Max 10 files.`;

        const { mutateAsync: removeFile } = useMutation<
            unknown,
            GivebutterAxiosError,
            { id: number }
        >({
            mutationFn: ({ id }) => {
                return deleteEngageMessageMedia(accountId, message.id, id);
            },
            onError: e => {
                error(e.response?.data.message || 'Something went wrong. Please try again.');
            },
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.engageMessageById(accountId, messageId),
                });
            },
        });

        const { mutate: addFile } = useMutation<unknown, GivebutterAxiosError, { file: File }>({
            mutationFn: ({ file }) => {
                const formData = new FormData();
                formData.append('files[]', file);
                return uploadEngageMessageMedia(accountId, message.id, formData);
            },
            onError: e => {
                error(e.response?.data.message || 'Something went wrong. Please try again.');
            },
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.engageMessageById(accountId, messageId),
                });
            },
        });

        return (
            <div
                className={twMerge(
                    'grid grid-cols-[80px_1fr] items-center border-b border-b-gray-100 py-2.5',
                    className,
                )}
                ref={forwardedRef}
                {...restProps}
            >
                <span className="text-sm text-gray-400">Files:</span>
                <div className={'flex max-w-[250px] flex-col gap-2'}>
                    <div className={twJoin(noImages && 'sr-only h-0')}>
                        <FilePond
                            ref={ref}
                            className="!mb-0 [&_.filepond--file-action-button.filepond--action-remove-item]:cursor-pointer"
                            allowImagePreview={false}
                            allowReorder={false}
                            allowFileEncode={false}
                            credits={false}
                            maxFiles={10}
                            files={message.media.map(file => ({
                                source: file.url,
                                options: {
                                    file: {
                                        name: file.name,
                                        size: file.size,
                                    },
                                    metadata: {
                                        id: file.id,
                                    },
                                    type: 'local',
                                },
                            }))}
                            allowMultiple
                            server={{
                                load: async (source, load) => {
                                    load(await getFile(source));
                                },
                            }}
                            acceptedFileTypes={[
                                'image/png',
                                'image/gif',
                                'image/jpeg',
                                'application/msword',
                                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                                'application/rtf',
                                'text/plain',
                                'application/pdf',
                            ]}
                            onaddfile={(_, file) => {
                                const id = file.getMetadata('id');
                                if (!id) addFile({ file: file.file as File });
                            }}
                            beforeRemoveFile={file => {
                                const id = file.getMetadata('id');
                                if (id) removeFile({ id });
                                return true;
                            }}
                            labelIdle={renderToString(
                                <div
                                    className={
                                        'flex cursor-pointer flex-col items-center justify-center'
                                    }
                                >
                                    <p className={'!text-sm'}>Click or drag to add an attachment</p>
                                    <p className={'!text-sm !font-medium'}>{attachment}</p>
                                </div>,
                            )}
                        />
                    </div>
                    {noImages ? (
                        <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                                <Button
                                    variant="none"
                                    color="primary"
                                    size="sm"
                                    className="font-medium"
                                    onClick={() => ref.current?.browse()}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    <p>Add attachment {attachment}</p>
                                </Button>
                            </Tooltip.Trigger>
                            <Tooltip.Content className={'text-sm'} inverse={true} side="bottom">
                                <Tooltip.Arrow inverse={true} />
                                <p>{tooltip}</p>
                            </Tooltip.Content>
                        </Tooltip.Root>
                    ) : (
                        <p className={'text-xs text-gray-600'}>{tooltip}</p>
                    )}
                </div>
            </div>
        );
    },
);

interface MessageBuilderSmsFilesProps extends ComponentPropsWithoutRef<'div'> {
    children?: never;
}
const MessageBuilderSmsFiles = forwardRef(
    (
        { className, ...restProps }: MessageBuilderSmsFilesProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { accountId } = useCurrentAccount();
        const { error } = useToast();
        const { messageId } = useContext(MessageBuilderContext);

        const { data: message } = useCurrentBuilderMessage();

        const firstMedia = first(message.media);
        const imageUrl = firstMedia?.url;

        const { mutate: removeFile } = useMutation<
            unknown,
            GivebutterAxiosError,
            { id: number | string }
        >({
            mutationFn: ({ id }) => {
                return deleteEngageMessageMedia(accountId, message.id, id);
            },
            onError: e => {
                error(e.response?.data.message || 'Something went wrong. Please try again.');
            },
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.engageMessageById(accountId, messageId),
                });
            },
        });

        const { mutateAsync: uploadFile } = useMutation<
            EngageMessageMedia,
            GivebutterAxiosError,
            { file: File }
        >({
            mutationFn: ({ file }) => {
                const formData = new FormData();
                formData.append('files[]', file);
                return uploadEngageMessageMedia(accountId, message.id, formData);
            },
            onError: e => {
                error(e.response?.data.message || 'Something went wrong. Please try again.');
            },
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.engageMessageById(accountId, messageId),
                });
            },
        });

        return (
            <div
                className={twMerge('flex flex-col gap-2 pt-6', className)}
                {...restProps}
                ref={forwardedRef}
            >
                <Label htmlFor="mediaUpload" className="text-gray-500">
                    Upload Media
                </Label>

                <Upload
                    defaultFiles={firstMedia && imageUrl ? [{ id: 'media', url: imageUrl }] : []}
                    variant={'single'}
                    acceptedFileTypes={['image/*']}
                    imagePreviewHeight={250}
                    allowReorder={false}
                    allowReplace={true}
                    allowMultiple={false}
                    // @ts-ignore
                    allowRemove={true}
                    strategy={'remote'}
                    uploadFn={async file => {
                        const response = await uploadFile({ file: file });
                        return response.url;
                    }}
                    onremovefile={() => {
                        const id = firstMedia?.id;
                        if (id) removeFile({ id });
                    }}
                    name="mediaUpload"
                    maxFileSize="1MB"
                />
            </div>
        );
    },
);

interface MessageBuilderSmsPreviewProps extends ComponentPropsWithoutRef<'div'> {
    children?: never;
}
const MessageBuilderSmsPreview = forwardRef(
    (
        { className, ...restProps }: MessageBuilderSmsPreviewProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { data: message } = useCurrentBuilderMessage();
        return <SmsMessagePreview message={message} ref={forwardedRef} {...restProps} />;
    },
);

interface MessageBuilderWysiwygProps {
    autoSaving?: boolean | null;
    autoSavedAt?: DateTime;
    autoResize?: boolean;
}
const MessageBuilderWysiwyg = ({
    autoSaving = false,
    autoSavedAt = DateTime.now(),
    autoResize = true,
}: MessageBuilderWysiwygProps) => {
    const apiUrl = useAppState(state => state.apiUrl);
    const hasFocused = useRef(false);
    const editorInstanceRef = useRef<TinyEditor['editor']>();
    const { variant } = useContext(MessageBuilderContext);
    const isSms = variant === 'sms';

    const { control, path, watch } = Form.useGivebutterFormContext();
    const body = watch('body');

    const charCount = sanitizeHtml(body, {
        allowedTags: [],
        allowedAttributes: {},
    }).trim().length;

    const { data: message } = useCurrentBuilderMessage();

    const toolbar = (isSms ? SMS_TOOLBAR : EMAIL_TOOLBAR).join(' ');

    const mediaUrlResolver = useCallback(
        ({ url }: { url: string }) => {
            const isUrlYoutube = (url?: string) => {
                return ['//youtube.com', '//www.youtube.com', '//youtu.be', '//www.youtu.be'].some(
                    yt => url?.includes(yt),
                );
            };
            return new Promise((resolve, reject) => {
                getMediaPreview(url)
                    .then(({ data: { title, images } }) => {
                        editorInstanceRef.current?.setDirty(true);

                        const isUrlYouTube = isUrlYoutube(url);
                        let imgSrc = `${apiUrl}/generate-video-thumbnail`;

                        // NOTE: temp solution while YouTube has issues with fetchLinkPreview
                        if (!images?.length && isUrlYouTube) {
                            // traditional YT url
                            let yt_id = url.split('v=')[1];

                            if (!yt_id) {
                                // try getting from short url
                                yt_id = url?.split('/').pop()?.split('?')[0] ?? '';
                            }

                            if (!yt_id) {
                                reject(new Error('Not a valid url, please try different one.'));
                            }

                            imgSrc += `?id=${yt_id}&type=youtube`;
                        } else {
                            imgSrc += `?url=${images[0]}`;
                        }

                        const html = `
                            <a href="${url}" title="${title}" target="_blank" rel="noopener noreferrer">
                                <img height="270px" width="480px" src="${imgSrc}" alt="Watch this video">
                            </a>
                        `.trim();

                        resolve({ html });
                    })
                    .catch(() => {
                        reject(new Error('Not a valid url, please try different one.'));
                    });
            });
        },
        [apiUrl],
    );

    if (message.editor !== 'basic') return null;

    return (
        <div className="w-full py-2.5">
            {autoSaving === null && !!autoSavedAt && (
                <p className="mb-2.5 flex items-center gap-1 text-sm font-medium text-red-500">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="mr-1.5" />

                    <span>
                        Auto saving failed. Last tried at{' '}
                        {autoSavedAt.toLocaleString(DateTime.TIME_SIMPLE)}.
                    </span>
                </p>
            )}

            {isSms && message.has_non_gsm_text && charCount > CHARACTER_SET_WARNING_COUNT && (
                <p className="mb-2.5 flex items-center gap-1 text-sm font-medium text-gray-900">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="mr-1.5" />

                    <span>
                        Some older carriers like US Cellular and C-Spire may deliver this as{' '}
                        {Math.ceil(charCount / CHARACTER_SET_WARNING_COUNT)} separate messages.{' '}
                        <GsmAlertDialog>
                            <AlertDialog.Trigger asChild>
                                <Button
                                    variant="none"
                                    color="neutral"
                                    className="border-b border-dashed border-black font-medium"
                                >
                                    Why?
                                </Button>
                            </AlertDialog.Trigger>
                        </GsmAlertDialog>
                    </span>
                </p>
            )}

            {autoSaving !== null && !!message.has_traceable_links && (
                <p className="animate__animated animate__fadeIn animate__fast mb-2.5 flex items-center gap-1 text-sm font-medium text-yellow-600">
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />

                    <span>
                        We'll automatically track revenue earned from this {isSms ? 'SMS' : 'email'}
                        .
                    </span>
                </p>
            )}

            {isSms &&
                autoSaving !== null &&
                !!message.has_links &&
                !message.has_traceable_links && (
                    <p className="mb-2.5 mr-1.5 flex items-center gap-1 text-sm font-medium text-yellow-600">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        <span>
                            We'll automatically shorten any links in this SMS using our short
                            domain, giveb.co.
                        </span>
                    </p>
                )}

            <Form.Error name={path('body')} />

            {!isSms ? <EmailHeader /> : null}

            <div
                className={twMerge(
                    `relative [&_.tox-editor-header]:!shadow-none [&_.tox-tinymce]:border-none`,
                    isSms ? '-ml-4 -mr-4' : '',
                )}
            >
                {isSms ? (
                    <div className="z-layerThree absolute right-0 top-2 flex items-center gap-2 text-sm">
                        <strong>Characters</strong>{' '}
                        <span
                            className={twMerge(
                                'rounded-full bg-gray-50 p-1 px-2',
                                charCount >= CHARACTER_LIMIT_COUNT - 10 &&
                                    'bg-orange-50 text-orange-500',
                                charCount >= CHARACTER_LIMIT_COUNT && 'bg-red-50 text-red-500',
                            )}
                        >
                            {charCount} of {CHARACTER_LIMIT_COUNT}
                        </span>
                    </div>
                ) : null}
                <WysiwygComp.Root>
                    <div className="z-layerOne relative">
                        <UseFormWysiwyg
                            control={control}
                            name="body"
                            rules={{ required: 'Message body is required' }}
                            inline={false}
                            autoResize={autoResize}
                            onInit={(_, editor) => (editorInstanceRef.current = editor)}
                            onFocus={() => (hasFocused.current = true)}
                            disableCustomPlugins={isSms ? ['canva'] : undefined}
                            sanitize={value => {
                                return isSms
                                    ? sanitizeHtml(value, {
                                          allowedTags: ['p'],
                                          allowedAttributes: {},
                                      })
                                    : value;
                            }}
                            init={{
                                media_dimensions: false,
                                menubar: false,
                                statusbar: false,
                                placeholder: 'Message...',
                                font_family_formats: 'Matter',
                                toolbar,
                                content_style: WysiwygComp.Constants.APP_FONT,
                                min_height: isSms ? 150 : undefined,
                                paste_as_text: isSms ? true : undefined,
                                entity_encoding: 'raw',
                                style_formats: [
                                    { title: 'Paragraph', format: 'p' },
                                    { title: 'Heading 1', format: 'h1' },
                                    { title: 'Heading 2', format: 'h2' },
                                    { title: 'Heading 3', format: 'h3' },
                                    { title: 'Heading 4', format: 'h4' },
                                    { title: 'Heading 5', format: 'h5' },
                                    { title: 'Heading 6', format: 'h6' },
                                    { title: 'Blockquote', format: 'blockquote' },
                                    {
                                        title: 'Link CTA styles (create a link first, then apply CTA style)',
                                    },
                                    { title: 'Call-to-action', format: 'calltoaction' },
                                ],
                                link_default_target: '_blank',
                                setup: function (editor) {
                                    editor.on('ExecCommand', event => {
                                        const command = event.command;
                                        if (command === 'mceMedia') {
                                            const tabElems = document.querySelectorAll(
                                                'div[role="tablist"] .tox-tab',
                                            );

                                            tabElems.forEach(tabElem => {
                                                const element = tabElem as HTMLElement;
                                                if (
                                                    ['Embed', 'Advanced'].includes(
                                                        element.innerText,
                                                    )
                                                ) {
                                                    element.style.display = 'none';
                                                }
                                            });
                                        }
                                    });
                                },
                                media_url_resolver: mediaUrlResolver,
                            }}
                        />
                    </div>
                </WysiwygComp.Root>
            </div>

            {!isSms ? <EmailFooter /> : null}
        </div>
    );
};

interface MessageBuilderAdvancedEmailEditorProps {
    onSave: (pageJson: string, pageHtml: string) => void;
    scrollPreview?: boolean;
    defaultOpen?: boolean;
}
const MessageBuilderAdvancedEmailEditor = ({
    onSave,
    scrollPreview = false,
    defaultOpen = false,
}: MessageBuilderAdvancedEmailEditorProps) => {
    const { accountId, hasGivebutterPlusAccess } = useCurrentAccount();
    const history = useHistory();
    const { apiUrl } = useAppState();
    const { data: message } = useCurrentBuilderMessage();
    const [showEditor, setShowEditor] = useState(defaultOpen);
    const [showPlusDialog, setShowPlusDialog] = useState(false);

    const handleOpenEditorClick = () => {
        if (!hasGivebutterPlusAccess) {
            setShowPlusDialog(true);
        } else {
            setShowEditor(true);
        }
    };

    if (message.editor === 'basic') return null;

    return (
        <div className="flex flex-col items-start gap-4 py-8 pb-4">
            {message.body ? (
                <>
                    <Button
                        onClick={handleOpenEditorClick}
                        variant="outline"
                        color="neutral"
                        className="w-full"
                    >
                        <FontAwesomeIcon icon={faEdit} />
                        Open email editor
                    </Button>
                    <div
                        className={twMerge(
                            'relative w-full overflow-auto',
                            scrollPreview && 'h-96 overflow-auto',
                        )}
                    >
                        <Badge className="absolute left-4 top-4 flex w-24 gap-2">
                            <FontAwesomeIcon icon={faEye} />
                            Preview
                        </Badge>

                        <IframeResizer
                            key={message.body}
                            autoResize={false}
                            className="h-full w-full"
                            src={`${apiUrl}/dashboard/accounts/${accountId}/messages/${message.id}/preview`}
                        />
                    </div>
                </>
            ) : (
                <div className="flex min-h-96 w-full flex-col items-center justify-center gap-4 rounded-xl bg-gray-50">
                    <FontAwesomeIcon
                        icon={faEnvelopeOpenText}
                        size="3x"
                        className="text-gray-300"
                    />
                    <div className="flex flex-col items-center gap-1">
                        <p className="text-sm font-medium text-gray-900">
                            An email preview will appear here
                        </p>
                        <p className="text-sm text-gray-600">
                            Open the email editor to begin creating your email
                        </p>
                    </div>
                    <Button variant="solid" onClick={handleOpenEditorClick}>
                        <FontAwesomeIcon icon={faEdit} />
                        Open email editor
                    </Button>
                </div>
            )}

            {showEditor && (
                <PrimitiveAdvancedEmailEditor
                    template={message.body_json ?? ''}
                    onSave={(json, html) => {
                        onSave(json, html);
                        setShowEditor(false);
                    }}
                />
            )}

            <AlertDialog.Root open={showPlusDialog} type="warning">
                <AlertDialog.Portal>
                    <AlertDialog.Overlay />
                    <AlertDialog.Content className="max-w-md">
                        <AlertDialog.Header>
                            <AlertDialog.Icon />
                            <AlertDialog.Close onClick={() => setShowPlusDialog(false)} />
                        </AlertDialog.Header>
                        <AlertDialog.Body className="flex flex-col gap-y-4">
                            <img
                                className={'max-w-48'}
                                src={GivebutterPlusLogo}
                                alt="Givebutter Plus logo"
                            />
                            <AlertDialog.Description className="flex flex-col gap-4">
                                <p>
                                    This message was created using the Visual Editor from Givebutter
                                    Plus. To continue editing or to send this message, you will need
                                    to upgrade.
                                </p>
                            </AlertDialog.Description>
                        </AlertDialog.Body>
                        <AlertDialog.Footer>
                            <AlertDialog.Action
                                onClick={() => {
                                    history.push(
                                        `/accounts/${accountId}/settings/about/subscription`,
                                    );
                                }}
                            >
                                View subscription settings
                            </AlertDialog.Action>
                        </AlertDialog.Footer>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>
        </div>
    );
};

const EmailHeader = () => {
    const { emailLogo, settings, name } = useCurrentAccount();
    return (
        <div
            className="mb-5 w-full p-6 pb-0 bg-blend-overlay"
            style={{
                background: `linear-gradient(0deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%), ${
                    settings.find(item => item.code === Settings.ThemeColor.Code)?.value ||
                    '#eaeef2'
                }`,
            }}
        >
            <div className="rounded-t-xl bg-white p-6">
                {emailLogo ? (
                    <img className="max-h-16 max-w-52" alt={name} src={emailLogo.url} />
                ) : null}
            </div>
        </div>
    );
};

const EmailFooter = () => {
    const { title, postalAddress } = useCurrentAccount();
    const { apiUrl } = useAppState();

    return (
        <div className="mt-5 flex flex-col gap-6 text-center">
            <div className="text-sm text-blue-500 underline">View this email in your browser.</div>
            <div className="h-1 w-full border-t border-t-gray-100" />

            <p className="mx-auto max-w-[450px] text-sm font-medium">
                You are receiving this email because you've previously supported or subscribed to
                emails from {title}.{' '}
                <span className="text-blue-500 underline">Unsubscribe here</span>
            </p>

            <a
                className="mx-auto inline-block h-8 max-w-44"
                target="_blank"
                rel="noopener noreferrer"
                href={apiUrl}
            >
                <img
                    alt="Powered by Givebutter"
                    src={`${apiUrl}/storage/branding/givebutter-badge.png`}
                />
            </a>

            <p className="text-xs font-medium text-gray-400">
                {title}
                <br />
                {postalAddress}
            </p>
        </div>
    );
};

const GsmAlertDialog = ({ children }: { children: ReactNode }) => {
    return (
        <AlertDialog.Root>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Network Carrier Support</AlertDialog.Title>
                        <AlertDialog.Description>
                            <div className="mb-4 flex w-full flex-col gap-4">
                                <p className="text-base">
                                    Most messages are sent using a standard known as{' '}
                                    <strong>GSM-7</strong>. We call this a{' '}
                                    <strong>character set</strong>. When this set is used, we can
                                    send up to 160 characters per SMS.
                                </p>

                                <p className="text-base">
                                    However, the <strong>GSM-7</strong> standard does not support
                                    characters like emoji (e.g. 🙂), diacritics (e.g.{' '}
                                    <strong>ç</strong>) and non-Latin scripts (e.g.{' '}
                                    <strong>Arabic</strong>).
                                </p>

                                <p className="text-base">
                                    To support these characters, we need to send messages using a
                                    different character set, known as <strong>UCS-2</strong>.
                                </p>
                            </div>

                            <Collapsible.Root>
                                <Collapsible.Trigger asChild>
                                    <Button
                                        variant="none"
                                        color="neutral"
                                        size="sm"
                                        className="font-medium"
                                    >
                                        Continue reading &rsaquo;
                                    </Button>
                                </Collapsible.Trigger>
                                <Collapsible.Content>
                                    <div className="mb-4 flex w-full flex-col gap-4">
                                        <p className="text-base">
                                            When this set is used, we can only send up to{' '}
                                            {CHARACTER_SET_WARNING_COUNT} characters per SMS.
                                            However, most carriers allow us to 'concatenate'
                                            messages so they are delivered as a single SMS.
                                        </p>

                                        <p className="text-base">
                                            Unfortunately, a few carriers don't support
                                            concatenation (most notably US Cellular, Google Voice
                                            and C-Spire).
                                        </p>

                                        <p className="text-base">
                                            For messages sent to cell numbers on these networks,
                                            each message will be delivered separately.
                                        </p>

                                        <p className="text-base">
                                            Unfortunately, in cases where we cannot concatenate
                                            messages, the delivery order cannot be guaranteed. This
                                            means an SMS can appear physically out of order.
                                        </p>

                                        <p className="text-base">
                                            These limitations are beyond our control. We recommend
                                            limiting your SMS to {CHARACTER_SET_WARNING_COUNT}{' '}
                                            characters if the carrier warning is visible.
                                        </p>

                                        <p className="text-base">
                                            If you have any questions, please email{' '}
                                            <a href="mailto:support@givebutter.com">
                                                support@givebutter.com
                                            </a>
                                        </p>
                                    </div>
                                </Collapsible.Content>
                            </Collapsible.Root>
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const Root = MessageBuilderRoot;
const Header = MessageBuilderHeader;
const HeaderActions = MessageBuilderHeaderActions;
const HeaderName = MessageBuilderHeaderName;
const Body = MessageBuilderBody;
const To = MessageBuilderTo;
const From = MessageBuilderFrom;
const SmsSender = MessageBuilderSmsSender;
const EmailSender = MessageBuilderEmailSender;
const Subject = MessageBuilderSubject;
const EmailFiles = MessageBuilderEmailFiles;
const SmsFiles = MessageBuilderSmsFiles;
const SmsPreview = MessageBuilderSmsPreview;
const Wysiwyg = MessageBuilderWysiwyg;
const AdvancedEmailEditor = MessageBuilderAdvancedEmailEditor;

export {
    MessageBuilderRoot,
    MessageBuilderHeader,
    MessageBuilderHeaderActions,
    MessageBuilderHeaderName,
    MessageBuilderBody,
    MessageBuilderTo,
    MessageBuilderFrom,
    MessageBuilderSmsSender,
    MessageBuilderEmailSender,
    MessageBuilderSubject,
    MessageBuilderEmailFiles,
    MessageBuilderSmsFiles,
    MessageBuilderSmsPreview,
    MessageBuilderWysiwyg,
    MessageBuilderAdvancedEmailEditor,
    //
    Root,
    Header,
    HeaderActions,
    HeaderName,
    Body,
    To,
    From,
    SmsSender,
    EmailSender,
    Subject,
    EmailFiles,
    SmsFiles,
    SmsPreview,
    Wysiwyg,
    AdvancedEmailEditor,
};
